import { numFormat, formatDate } from '@/utils';
import { Node, Descendant } from 'slate';
import {
  HiMessage,
  HiMessageLocal,
  HiOfferInfo,
  HiOfferInfoLocal,
  HiSession,
  HiUserInfo,
  HiMessageDataMaybeWeired,
  SystemMessageDataMaybeWeired,
} from './types';

export const SAY_HI_SHARE_LINK = `${window.location.origin}/hi`;

export function convertHiMessageToLocal(
  message: HiMessage,
  extras: {
    session_id: HiSession['id'];
    other_side_id: string;
    my_id: string;
  },
): HiMessageLocal {
  return {
    id: message.id,
    session_id: extras.session_id,
    sendor_id: message.from_user_id, // 最后一条消息对方发送
    target_id: extras.other_side_id,

    attributes: {
      is_from_me: message.from_user_id === extras.my_id,
      is_previewable: true,
      is_from_system: message.from_user_id === 'system',
      is_unread_sensitive: true,
      is_other_read: message.other_is_read,
      is_offer_by_other: message.type === 'offer_charged',
      is_bonus_received: message.type === 'bonus_received',
      isOfferBoost: message.type === 'offer_boost',
      offer_status: message.offer?.status,
      is_scam: Boolean(message.is_scam),
    },
    type: message.offer?.id ? 'offer' : 'normal',
    data: message.data,
    data_translation: message.data_translation,
    parsedData: parseHiMessageData(message.data),
    updated_at: Date.now(),
    offer: !message.offer ? null : convertHiOfferToLocal(message.offer),
    sending_time: message.create_at,
  };
}

export function convertHiOfferToLocal(offer: Partial<HiOfferInfo>): HiOfferInfoLocal | null {
  if (!offer?.id) {
    return null;
  }
  return {
    id: offer.id,
    price: offer.price || 0,
    status: offer.status || 'uncharged',
    created_at: offer.create_at || 0,
    updated_at: offer.update_at || 0,
    expired_at: offer.expire_at || 0,
    bonus: offer.bonus || 0,
  };
}

export function isSystemMessage(message: HiMessage) {
  return message.from_user_id === 'system';
}

export function isUnreadMessageByOtherSide(message: HiMessage, my_id: HiUserInfo['id']) {
  return my_id && message.from_user_id !== my_id && !message.other_is_read;
}

export function isUnchargedOfferMessage(message: HiMessage): boolean {
  return (
    message.offer?.status === 'uncharged' ||
    (message.offer?.status === 'expired' && !message.is_open)
  );
}

export function isMessageByMe(message: HiMessage, myId?: HiUserInfo['id']): boolean {
  return !!myId && message.from_user_id === myId;
}

export function formatOfferPrice(offer: Partial<HiMessage['offer']> | null, prefix = '$'): string {
  return formatOfferPriceNumber(offer?.price, prefix);
}

export function formatOfferPriceNumber(price?: number, prefix = ''): string {
  return numFormat((price || 0) / 1e4, 2, prefix);
}

export function parseHiMessageData(_rawData?: HiMessage['data']): HiMessageLocal['parsedData'] {
  const rawData = _rawData as HiMessageDataMaybeWeired;
  let type = rawData?.type || ('unknown' as const);
  let value = rawData?.value?.data || null;
  let textValue = `${rawData?.value?.data?.toString()}${rawData?.value?.image ? ' [Picture]' : ''}`;
  const url = rawData?.value?.url;
  const image = rawData?.value?.image;

  switch (type) {
    case 'text':
      break;
    case 'richtext':
      const nodes: Descendant[] = JSON.parse(value || '') || [];
      textValue = nodes.map(n => Node.string(n)).join('\n');
      break;
    case 'media':
      textValue = '';
      break;
    default:
      type = 'unknown';
      textValue = '';
      break;
  }

  return {
    type,
    value,
    url,
    image,
    textValue,
  };
}

export function parseSystemMessageData(rawData?: HiMessage['data']) {
  return {
    offer: (rawData as SystemMessageDataMaybeWeired)?.offer || null,
    price: (rawData as SystemMessageDataMaybeWeired)?.price || 0,
  };
}

export const formatMessageDate = (time: number) => formatDate(time * 1000, 'MM/DD HH:mm:ss');

export function getLastMessageTimeFromSession(session: HiSession) {
  return Math.max(
    session.last_display_message?.create_at || 0,
    session.last_display_message?.offer?.update_at || 0,
    session.update_at,
  );
}

export function getExpiredDaysCount(startSecond: number = 0, endSecond: number = 0) {
  return Math.round((endSecond - startSecond) / 60 / 60 / 24);
}
